/** @jsxImportSource @emotion/react */
import { styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css, type CSSObject } from '@emotion/react';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { useEffect, useId, useRef, useState } from 'react';

import { InfoIcon as KaiShikiInfoIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { SettingsToggle } from '@/Client/Widgets/Settings';
import { CommonFieldAdornment, CommonFieldInput, CommonFieldShell } from '@kaiizen/kai-shiki/react';
import {
  ORG_SETTINGS_BODY_TEXT_STYLE,
  ORG_SETTINGS_COLORS,
  ORG_SETTINGS_PANEL_HEADER_STYLE,
  ORG_SETTINGS_PANEL_STYLE,
  ORG_SETTINGS_SECTION_INSET_STYLE,
  ORG_SETTINGS_SUBHEADING_STYLE,
} from './OrgSettingsStyleLib';

export type InputsTradeGroupItem = {
  slug: string;
  defaultLabel: string;
  customLabel: string;
  characterCountLabel: string;
  enabled: boolean;
};

export type InputsTradeGroup = {
  key: string;
  title: string;
  enabled: boolean;
  items: readonly InputsTradeGroupItem[];
};

type InputsTradeGroupsPanelProps = {
  groups: readonly InputsTradeGroup[];
  disabled?: boolean;
  onCustomLabelChange?: (tradeSlug: string, value: string) => void;
  /** Autosave commits a pending label here rather than waiting out the pause (KAIDEV-4345). */
  onCustomLabelBlur?: (tradeSlug: string) => void;
  onCustomLabelFocus?: (tradeSlug: string) => void;
  onToggleClick?: (tradeSlug: string) => void;
  onGroupToggleClick?: (groupKey: string) => void;
};

const PANEL_WRAPPER_STYLE = css`
  label: PANEL_WRAPPER_STYLE;
  ${ORG_SETTINGS_SECTION_INSET_STYLE}
`;

const PANEL_STACK_STYLE = css`
  label: PANEL_STACK_STYLE;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const GROUP_SECTION_STYLE = css`
  label: GROUP_SECTION_STYLE;
  width: 100%;
`;

const GROUP_HEADER_STYLE = css`
  label: GROUP_HEADER_STYLE;
  ${ORG_SETTINGS_PANEL_HEADER_STYLE}
  justify-content: space-between;
  gap: 16px;
`;

const GROUP_HEADER_DISABLED_STYLE = css`
  label: GROUP_HEADER_DISABLED_STYLE;
  color: ${ORG_SETTINGS_COLORS.textMutedLight};
`;

const GROUP_TITLE_STYLE = css`
  label: GROUP_TITLE_STYLE;
  ${ORG_SETTINGS_SUBHEADING_STYLE}
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.25;
  letter-spacing: 0;
`;

const ITEM_ROW_STYLE = css`
  label: ITEM_ROW_STYLE;
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  min-height: 70px;
  padding: 8px 20px;
  border-bottom: 1px solid ${ORG_SETTINGS_COLORS.border};

  &:last-of-type {
    border-bottom: 0;
  }

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 12px;
  }
`;

const ITEM_ROW_DISABLED_STYLE = css`
  label: ITEM_ROW_DISABLED_STYLE;
  opacity: 0.65;
`;

const DEFAULT_LABEL_STYLE = css`
  label: DEFAULT_LABEL_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_STYLE}
  color: ${ORG_SETTINGS_COLORS.text};
  min-width: 0;

  @media (max-width: 960px) {
    grid-column: 1 / -1;
  }
`;

const DEFAULT_LABEL_DISABLED_STYLE = css`
  label: DEFAULT_LABEL_DISABLED_STYLE;
  color: ${ORG_SETTINGS_COLORS.textMutedLight};
`;

const INPUT_SHELL_STYLE = {
  position: 'relative',
  minHeight: 44,
  width: '100%',
  maxWidth: 700,
  padding: '10px 82px 10px 14px',
  border: `1px solid ${ORG_SETTINGS_COLORS.textFieldLabel}`,
  borderRadius: 0,
  background: ORG_SETTINGS_COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'start',
} satisfies CSSObject;

const INPUT_SHELL_DISABLED_STYLE = {
  borderColor: ORG_SETTINGS_COLORS.border,
  background: ORG_SETTINGS_COLORS.pageBackground,
} satisfies CSSObject;

const INPUT_TEXT_STYLE = {
  label: 'INPUT_TEXT_STYLE',
  width: '100%',
  border: 0,
  padding: 0,
  background: 'transparent',
  outline: 'none',
  fontFamily: FONT.body,
  fontSize: `${pxToRem(FONT_SIZE.bodySmall)} !important`,
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.regular,
  lineHeight: 1.333,
  letterSpacing: '0.24px',
  color: ORG_SETTINGS_COLORS.text,
  '&::placeholder': {
    fontSize: pxToRem(FONT_SIZE.bodyMicro),
    lineHeight: 1.6,
    letterSpacing: '0.2px',
    color: ORG_SETTINGS_COLORS.textSoft,
    opacity: 1,
  },
} satisfies CSSObject;

const INPUT_TEXT_DISABLED_STYLE = {
  color: ORG_SETTINGS_COLORS.textMutedLight,
  cursor: 'not-allowed',
  '&::placeholder': {
    color: ORG_SETTINGS_COLORS.textMutedLight,
  },
} satisfies CSSObject;

const INPUT_META_STYLE = {
  position: 'absolute',
  top: '50%',
  right: 10,
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  pointerEvents: 'auto',
} satisfies CSSObject;

const CHARACTER_COUNT_STYLE = {
  fontFamily: FONT.body,
  fontSize: pxToRem(FONT_SIZE.bodyMicro),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.regular,
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: ORG_SETTINGS_COLORS.textFieldLabel,
  flex: '0 0 auto',
} satisfies CSSObject;

const INFO_POPOVER_STYLE = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
} satisfies CSSObject;

const INFO_BUTTON_STYLE = {
  width: 20,
  height: 20,
  borderRadius: 999,
  border: 0,
  background: 'transparent',
  color: ORG_SETTINGS_COLORS.text,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  cursor: 'pointer',
  transition: 'opacity 120ms ease',
  '&:hover': {
    opacity: 0.8,
  },
  '&:focus-visible': {
    outline: `2px solid ${ORG_SETTINGS_COLORS.textFieldLabel}`,
    outlineOffset: 2,
  },
} satisfies CSSObject;

const INFO_BUTTON_OPEN_STYLE = {
  opacity: 0.8,
} satisfies CSSObject;

const INFO_TOOLTIP_STYLE = css`
  label: INFO_TOOLTIP_STYLE;
  position: absolute;
  right: -12px;
  bottom: calc(100% + 12px);
  width: 232px;
  padding: 12px 14px;
  border-radius: 0;
  background: ${ORG_SETTINGS_COLORS.textFieldLabel};
  color: ${ORG_SETTINGS_COLORS.white};
  text-align: center;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-style: normal;
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.333;
  letter-spacing: 0.24px;
  box-shadow: 0 12px 24px rgba(29, 29, 21, 0.14);
  z-index: 2;
`;

const INFO_TOOLTIP_ARROW_STYLE = css`
  label: INFO_TOOLTIP_ARROW_STYLE;
  position: absolute;
  right: 18px;
  top: 100%;
  width: 12px;
  height: 8px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: -6px;
    width: 12px;
    height: 12px;
    background: ${ORG_SETTINGS_COLORS.textFieldLabel};
    transform: rotate(45deg);
  }
`;

const INFO_TEXT = 'Custom labels replace the default trade name throughout Kai. Clear this field any time to go back to the original label.';

const InfoIcon = () => <KaiShikiInfoIcon size={18} />;

const InputsTradeRow = ({
  item,
  disabled = false,
  onCustomLabelChange,
  onCustomLabelBlur,
  onCustomLabelFocus,
  onToggleClick,
}: {
  item: InputsTradeGroupItem;
  disabled?: boolean;
  onCustomLabelChange?: (tradeSlug: string, value: string) => void;
  onCustomLabelBlur?: (tradeSlug: string) => void;
  onCustomLabelFocus?: (tradeSlug: string) => void;
  onToggleClick?: (tradeSlug: string) => void;
}) => {
  const enabled = item.enabled;
  const inputDisabled = disabled || !enabled;
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const infoPopoverRef = useRef<HTMLDivElement | null>(null);
  const infoTooltipId = useId();

  useEffect(() => {
    if (!isInfoOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!infoPopoverRef.current?.contains(event.target as Node)) {
        setIsInfoOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isInfoOpen]);

  return (
    <div css={[ITEM_ROW_STYLE, !enabled && ITEM_ROW_DISABLED_STYLE]}>
      <div css={[DEFAULT_LABEL_STYLE, !enabled && DEFAULT_LABEL_DISABLED_STYLE]}>
        {item.defaultLabel}
      </div>

      <CommonFieldShell
        css={[
          INPUT_SHELL_STYLE,
          inputDisabled && INPUT_SHELL_DISABLED_STYLE,
        ]}
      >
        <CommonFieldInput
          css={[INPUT_TEXT_STYLE, inputDisabled && INPUT_TEXT_DISABLED_STYLE]}
          value={item.customLabel}
          maxLength={25}
          placeholder='Enter custom label'
          disabled={inputDisabled}
          onChange={event => onCustomLabelChange?.(item.slug, event.target.value)}
          onBlur={() => onCustomLabelBlur?.(item.slug)}
          onFocus={() => onCustomLabelFocus?.(item.slug)}
        />
        <CommonFieldAdornment css={INPUT_META_STYLE}>
          <span css={CHARACTER_COUNT_STYLE}>{item.characterCountLabel}</span>
          <div css={INFO_POPOVER_STYLE} ref={infoPopoverRef}>
            <button
              type='button'
              css={[INFO_BUTTON_STYLE, isInfoOpen && INFO_BUTTON_OPEN_STYLE]}
              aria-label='View custom label details'
              aria-expanded={isInfoOpen}
              aria-controls={infoTooltipId}
              disabled={disabled}
              onClick={() => setIsInfoOpen(current => !current)}
            >
              <InfoIcon />
            </button>
            {isInfoOpen
              ? (
                <div id={infoTooltipId} css={INFO_TOOLTIP_STYLE} role='tooltip'>
                  {INFO_TEXT}
                  <div css={INFO_TOOLTIP_ARROW_STYLE} />
                </div>
              )
              : null}
          </div>
        </CommonFieldAdornment>
      </CommonFieldShell>

      <SettingsToggle
        checked={enabled}
        aria-label={`Enable ${item.defaultLabel}`}
        disabled={disabled}
        onClick={() => onToggleClick?.(item.slug)}
      />
    </div>
  );
};

const InputsTradeGroupSection = ({
  group,
  disabled = false,
  onCustomLabelChange,
  onCustomLabelBlur,
  onCustomLabelFocus,
  onToggleClick,
  onGroupToggleClick,
}: {
  group: InputsTradeGroup;
  disabled?: boolean;
  onCustomLabelChange?: (tradeSlug: string, value: string) => void;
  onCustomLabelBlur?: (tradeSlug: string) => void;
  onCustomLabelFocus?: (tradeSlug: string) => void;
  onToggleClick?: (tradeSlug: string) => void;
  onGroupToggleClick?: (groupKey: string) => void;
}) => (
  <div css={GROUP_SECTION_STYLE}>
    <div css={[GROUP_HEADER_STYLE, !group.enabled && GROUP_HEADER_DISABLED_STYLE]}>
      <div css={GROUP_TITLE_STYLE}>{group.title}</div>
      <SettingsToggle
        checked={group.enabled}
        aria-label={`Toggle ${group.title}`}
        disabled={disabled}
        onClick={() => onGroupToggleClick?.(group.key)}
      />
    </div>

    {group.items.map(item => (
      <InputsTradeRow
        key={item.slug}
        item={item}
        disabled={disabled}
        onCustomLabelChange={onCustomLabelChange}
        onCustomLabelBlur={onCustomLabelBlur}
        onCustomLabelFocus={onCustomLabelFocus}
        onToggleClick={onToggleClick}
      />
    ))}
  </div>
);

export const InputsTradeGroupsPanel = ({
  groups,
  disabled = false,
  onCustomLabelChange,
  onCustomLabelBlur,
  onCustomLabelFocus,
  onToggleClick,
  onGroupToggleClick,
}: InputsTradeGroupsPanelProps) => (
  <div css={PANEL_WRAPPER_STYLE}>
    <div css={PANEL_STACK_STYLE}>
      {groups.map(group => (
        <div key={group.key} css={ORG_SETTINGS_PANEL_STYLE}>
          <InputsTradeGroupSection
            group={group}
            disabled={disabled}
            onCustomLabelChange={onCustomLabelChange}
            onCustomLabelBlur={onCustomLabelBlur}
            onCustomLabelFocus={onCustomLabelFocus}
            onToggleClick={onToggleClick}
            onGroupToggleClick={onGroupToggleClick}
          />
        </div>
      ))}
    </div>
  </div>
);
